import { useEditor } from "@tiptap/react";
import { useEffect, useMemo, useRef } from "react";
import { buildEditorExtensions } from "../editor-config";
import { replaceVariableSpansWithBraces } from "../../tiptap/variable";

/**
 * Hook for initializing and managing the TipTap editor instance
 * Handles editor setup, content synchronization, and lifecycle
 */
export function useRichTextEditor(
  value: string,
  onChange: (value: string) => void,
  placeholder: string,
  disabled: boolean
) {
  const previousValueRef = useRef<string>(value);

  // Build extensions once and de-duplicate by name to avoid TipTap warnings
  const editorExtensions = useMemo(() => {
    return buildEditorExtensions(placeholder);
  }, [placeholder]);

  const editor = useEditor({
    extensions: editorExtensions,
    content: value,
    onUpdate: ({ editor }) => {
      // Ensure variables serialize back to {{var}}
      const html = editor.getHTML();
      onChange(replaceVariableSpansWithBraces(html));
    },
    editable: !disabled,
    immediatelyRender: false, // Fix SSR hydration mismatch
  });

  // Update editor content when value prop changes
  useEffect(() => {
    if (!editor || !value) return;

    // Only update if the value actually changed and is different from editor content
    if (previousValueRef.current !== value) {
      const currentContent = editor.getHTML();

      // Normalize content for comparison (remove extra whitespace/formatting differences)
      const normalizeContent = (content: string) => content.replace(/\s+/g, " ").trim();

      // TipTap renders variables as spans; convert spans back to {{var}} before comparison
      const normalizedCurrent = normalizeContent(replaceVariableSpansWithBraces(currentContent));
      const normalizedIncoming = normalizeContent(value);

      if (normalizedCurrent !== normalizedIncoming) {
        // Use requestAnimationFrame instead of setTimeout for better performance
        requestAnimationFrame(() => {
          if (!editor.isDestroyed) {
            editor.commands.setContent(value, { emitUpdate: false });
          }
        });
      }

      previousValueRef.current = value;
    }
  }, [editor, value]);

  return editor;
}
